'use client'

import {
  MixpanelOobTokenFragment,
  MixpanelTokenFragment,
  ProjectDisplayFragment,
  Render_Types_Enum,
  SoloViewerOobTokenFragment
} from '@/gql/generated/graphql'
import { cn } from '@/lib/utils'
import { PaginationButton, PaginationDirection } from '@components/pagination-button'
import { TokenDisplay } from '@components/token-display'
import { ElementWithFullscreenFunctions, useFullscreen } from '@components/token-display/hooks/use-fullscreen'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { generateRandomTokenData, getNonLiveDisplayType, SoloViewerToken } from '@utils/common'
import { constructLiveViewUrlWithHash } from '@utils/routes'
import { AnimatePresence, motion } from 'motion/react'
import * as React from 'react'
import { isOob } from '.'
import { DisplayNotes } from './display-notes'
import { SoloViewFooter } from './solo-view-footer'

type SoloDisplayProps = {
  token: SoloViewerToken
  project: ProjectDisplayFragment
  numTokens: number
  onChangeToken?: (direction: PaginationDirection) => void
  className?: string
}
export const generatedSampleId = 'generated'
export const SoloDisplay = ({
  token,
  project,
  numTokens,
  onChangeToken
}: SoloDisplayProps): React.ReactElement | null => {
  const [showLiveView, setShowLiveView] = React.useState<boolean>(
    project.primary_render_type === Render_Types_Enum.Live || !token.image_id
  )

  const [currToken, setCurrToken] = React.useState<SoloViewerToken>(token)

  const onGenerate = () => {
    const generatedToken: SoloViewerOobTokenFragment = {
      id: generatedSampleId,
      token_id: 0,
      live_view_url: '',
      project
    }
    const { hash, tokenId } = generateRandomTokenData(project?.index ?? 0, project?.max_invocations ?? 0)
    generatedToken.token_id = parseInt(tokenId)
    generatedToken.live_view_url = constructLiveViewUrlWithHash(project.id ?? '', tokenId, hash)
    setCurrToken(generatedToken)
  }

  React.useEffect(() => {
    setCurrToken(token)
  }, [token.token_id])

  const [openDisplayNotes, setOpenDisplayNotes] = React.useState<boolean>(false)

  const { frameRefEl, onFullScreenClick } = useFullscreen<ElementWithFullscreenFunctions>()

  const toggleLiveView = () => {
    if (!currToken.media_url) {
      return
    }
    trackButtonClicked(TrackedButtonNames.toggleLiveView, {
      project,
      token: !isOob(token) ? (token as MixpanelTokenFragment) : undefined,
      oobToken: isOob(token) ? (token as MixpanelOobTokenFragment) : undefined,
      customProperties: {
        'Toggling off': showLiveView
      }
    })
    setShowLiveView(!showLiveView)
  }

  const toggleDisplayNotes = () => {
    trackButtonClicked(TrackedButtonNames.toggleDisplayNotes, {
      project,
      token: !isOob(currToken) ? (currToken as MixpanelTokenFragment) : undefined,
      oobToken: isOob(currToken) ? (currToken as MixpanelOobTokenFragment) : undefined,
      customProperties: {
        'Toggling off': openDisplayNotes
      }
    })
    setOpenDisplayNotes(!openDisplayNotes)
  }

  const [windowWidth, setWindowWidth] = React.useState<number>(typeof window !== undefined ? window.innerWidth : 0)

  // Close display notes on resize (prevents weird canvas behavior... trust me on this one)
  React.useEffect(() => {
    function handleResize() {
      setOpenDisplayNotes(false)
      setWindowWidth(typeof window !== undefined ? window.innerWidth : 0)
    }

    if (typeof window === 'undefined') {
      return
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <AnimatePresence>
      {windowWidth > 768 ? (
        <div className="gap-y-md relative flex h-full w-full flex-col items-center" key={'desktop-solo-display'}>
          <div className="relative w-full flex-1">
            <motion.div
              className={cn('absolute inset-0')}
              key={token.token_id}
              initial={{ opacity: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              animate={{
                scale: openDisplayNotes ? 0.75 : 1,
                // With content scaled to 75% and notes taking 25% width,
                // 12.5% shift (half of notes width) centers content in remaining space
                x: openDisplayNotes ? '12.5%' : 0,
                opacity: 1
              }}
              transition={{
                type: 'tween',
                duration: 0.3
              }}
              ref={frameRefEl}
            >
              <TokenDisplay
                token={currToken}
                project={project}
                preferredDisplayType={showLiveView ? Render_Types_Enum.Live : getNonLiveDisplayType(project, currToken)}
                autoPlay={true}
              />
            </motion.div>
            <AnimatePresence>
              {openDisplayNotes && (
                <DisplayNotes
                  project={project}
                  token={currToken}
                  onClose={() => {
                    setOpenDisplayNotes(false)
                  }}
                  entryDirection="left"
                  className={`absolute left-[-24px] top-[-24px] h-[100dvh] w-1/4 ${
                    openDisplayNotes ? 'block' : 'hidden'
                  }`}
                  liveViewActive={showLiveView}
                  onFullScreenClick={onFullScreenClick}
                />
              )}
            </AnimatePresence>
            {numTokens > 1 && (
              <>
                <div className="absolute left-[-16px] top-0 z-[60] flex h-full flex-col justify-center" key="left">
                  <motion.div
                    animate={{
                      opacity: openDisplayNotes ? 0 : 1,
                      scale: openDisplayNotes ? 0 : 1
                    }}
                    transition={{ type: 'spring', duration: 1 }}
                  >
                    <PaginationButton
                      direction={PaginationDirection.Left}
                      onClick={(event) => {
                        event.stopPropagation()
                        onChangeToken?.(PaginationDirection.Left)
                        trackButtonClicked(TrackedButtonNames.paginateLeft, {
                          token: !isOob(currToken) ? (currToken as MixpanelTokenFragment) : undefined,
                          oobToken: isOob(currToken) ? (currToken as MixpanelOobTokenFragment) : undefined,
                          project
                        })
                      }}
                    />
                  </motion.div>
                </div>
                <div className="absolute right-[-16px] top-0 z-[60] flex h-full flex-col justify-center" key="right">
                  <motion.div
                    animate={{
                      opacity: openDisplayNotes ? 0 : 1,
                      scale: openDisplayNotes ? 0 : 1
                    }}
                    transition={{ type: 'spring', duration: 1 }}
                  >
                    <PaginationButton
                      direction={PaginationDirection.Right}
                      onClick={(event) => {
                        event.stopPropagation()
                        onChangeToken?.(PaginationDirection.Right)
                        trackButtonClicked(TrackedButtonNames.paginateRight, {
                          token: !isOob(currToken) ? (currToken as MixpanelTokenFragment) : undefined,
                          oobToken: isOob(currToken) ? (currToken as MixpanelOobTokenFragment) : undefined,
                          project
                        })
                      }}
                    />
                  </motion.div>
                </div>
              </>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              paddingLeft: openDisplayNotes ? '25%' : '0px'
            }}
            className="w-full"
          >
            <SoloViewFooter
              token={currToken}
              project={project}
              showLiveView={showLiveView}
              toggleLiveView={toggleLiveView}
              toggleDisplayNotes={toggleDisplayNotes}
              onGenerate={onGenerate}
            />
          </motion.div>
        </div>
      ) : (
        <div
          className="gap-y-md relative flex h-full w-full flex-col items-center justify-center md:hidden"
          key="mobile-solo-display"
        >
          <motion.div
            className="relative flex h-full w-full items-center justify-center"
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            animate={{
              scale: openDisplayNotes ? 0.5 : 1,
              // Arbitrary value to give the illusion of a "slide up"
              y: openDisplayNotes ? -100 : 0,
              opacity: 1
            }}
            transition={{
              type: 'tween',
              duration: 0.3
            }}
            ref={frameRefEl}
          >
            <TokenDisplay
              token={currToken}
              project={project}
              preferredDisplayType={showLiveView ? Render_Types_Enum.Live : getNonLiveDisplayType(project, currToken)}
              autoPlay={true}
            />
          </motion.div>
          <motion.div
            layout
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              y: openDisplayNotes ? -450 : 0
            }}
            transition={{
              type: 'tween',
              duration: 0.3
            }}
            className="w-full"
          >
            <SoloViewFooter
              token={currToken}
              project={project}
              showLiveView={showLiveView}
              toggleLiveView={toggleLiveView}
              toggleDisplayNotes={toggleDisplayNotes}
              onGenerate={onGenerate}
            />
          </motion.div>
          <AnimatePresence>
            {openDisplayNotes && (
              <DisplayNotes
                project={project}
                token={currToken}
                onClose={() => {
                  setOpenDisplayNotes(false)
                }}
                entryDirection="bottom"
                className={`bg-surface-inverted absolute bottom-[-24px] left-[-24px] z-[80] h-[100dvh] w-screen ${
                  openDisplayNotes ? 'block' : 'hidden'
                }`}
                liveViewActive={showLiveView}
                onFullScreenClick={onFullScreenClick}
              />
            )}
          </AnimatePresence>
          {numTokens > 1 && (
            <>
              <div className="absolute left-[-28px] z-10 flex h-full flex-col justify-center" key="left">
                <motion.div
                  animate={{
                    opacity: openDisplayNotes ? 0 : 1,
                    scale: openDisplayNotes ? 0 : 1
                  }}
                  className="z-[60]"
                  transition={{ type: 'spring', duration: 1 }}
                >
                  <PaginationButton
                    direction={PaginationDirection.Left}
                    onClick={(event) => {
                      event.stopPropagation()
                      onChangeToken?.(PaginationDirection.Left)
                      trackButtonClicked(TrackedButtonNames.paginateLeft, {
                        token: !isOob(currToken) ? (currToken as MixpanelTokenFragment) : undefined,
                        oobToken: isOob(currToken) ? (currToken as MixpanelOobTokenFragment) : undefined,
                        project
                      })
                    }}
                  />
                </motion.div>
              </div>
              <div className="absolute right-[-28px] z-10 flex h-full flex-col justify-center" key="right">
                <motion.div
                  animate={{
                    opacity: openDisplayNotes ? 0 : 1,
                    scale: openDisplayNotes ? 0 : 1
                  }}
                  transition={{ type: 'spring', duration: 1 }}
                  className="z-[60]"
                >
                  <PaginationButton
                    direction={PaginationDirection.Right}
                    onClick={(event) => {
                      event.stopPropagation()
                      onChangeToken?.(PaginationDirection.Right)
                      trackButtonClicked(TrackedButtonNames.paginateRight, {
                        token: !isOob(currToken) ? (currToken as MixpanelTokenFragment) : undefined,
                        oobToken: isOob(currToken) ? (currToken as MixpanelOobTokenFragment) : undefined,
                        project
                      })
                    }}
                  />
                </motion.div>
              </div>
            </>
          )}
        </div>
      )}
    </AnimatePresence>
  )
}
